import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { AppShell } from "@/components/layouts/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { resetPassword } from "@/lib/authClient";

const searchSchema = z.object({
  email: z.string().email().optional(),
});

export const Route = createFileRoute("/reset-password")({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();

  const [email, setEmail] = useState(search.email ?? "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setSubmitting(true);
    try {
      await resetPassword({
        email: email.trim().toLowerCase(),
        code,
        newPassword,
      });
      void navigate({
        to: "/login" as never,
        search: { returnTo: "/app" } as never,
        replace: true,
      });
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorMsg(e.problem.detail ?? "Could not reset password.");
      } else {
        setErrorMsg("Could not reset password.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell>
      <section className="container py-16 md:py-20">
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <CardTitle className="font-serif text-3xl text-palm">Set a new password</CardTitle>
            <CardDescription>
              Enter the code from your email and choose a new password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Verification code</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="text-center text-2xl tracking-widest"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={submitting}
                />
              </div>

              {errorMsg && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {errorMsg}
                </div>
              )}

              <Button type="submit" size="lg" className="w-full" variant="accent" disabled={submitting || code.length !== 6}>
                {submitting ? "Resetting…" : "Reset password"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                <Link to={"/login" as never} className="text-palm underline underline-offset-2">
                  Back to sign in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </section>
    </AppShell>
  );
}
